import { isEqual } from 'lodash';
import { locationService } from '@grafana/runtime';
import { SceneObjectStateChangedEvent } from '../core/events.js';
import { writeSceneLog } from '../utils/writeSceneLog.js';

class UrlSyncManager {
  constructor() {
    this.urlKeyMapper = new UniqueUrlKeyMapper();
    this._stateSub = null;
    this._locationSub = null;
    this._onLocationUpdate = (location) => {
      if (this._lastPath !== location.pathname) {
        return;
      }
      const urlParams = new URLSearchParams(location.search);
      this.urlKeyMapper.rebuildIndex(this._sceneRoot);
      this._syncSceneStateFromUrl(this._sceneRoot, urlParams);
      this._lastPath = location.pathname;
    };
    this._onStateChanged = ({ payload }) => {
      const changedObject = payload.changedObject;
      if (changedObject.urlSync) {
        const newUrlState = changedObject.urlSync.getUrlState();
        const searchParams = locationService.getSearch();
        const mappedUpdated = {};
        this.urlKeyMapper.rebuildIndex(this._sceneRoot);
        for (const [key, newUrlValue] of Object.entries(newUrlState)) {
          const uniqueKey = this.urlKeyMapper.getUniqueKey(key, changedObject);
          const currentUrlValue = searchParams.getAll(uniqueKey);
          if (!isUrlValueEqual(currentUrlValue, newUrlValue)) {
            mappedUpdated[uniqueKey] = newUrlValue;
          }
        }
        if (Object.keys(mappedUpdated).length > 0) {
          locationService.partial(mappedUpdated, true);
        }
      }
    };
  }
  initSync(root) {
    if (!this._locationSub) {
      writeSceneLog("UrlSyncManager", "New location listen");
      this._locationSub = locationService.getHistory().listen(this._onLocationUpdate);
    }
    if (this._stateSub) {
      writeSceneLog("UrlSyncManager", "Unregister previous scene state subscription", this._sceneRoot.state.key);
      this._stateSub.unsubscribe();
    }
    this._sceneRoot = root;
    this._lastPath = locationService.getLocation().pathname;
    this._stateSub = root.subscribeToEvent(SceneObjectStateChangedEvent, this._onStateChanged);
    this.syncFrom(this._sceneRoot);
  }
  cleanUp(root) {
    if (this._sceneRoot !== root) {
      return;
    }
    writeSceneLog("UrlSyncManager", "Clean up");
    if (this._locationSub) {
      this._locationSub();
      writeSceneLog("UrlSyncManager", "Unregister history listen");
      this._locationSub = null;
    }
    if (this._stateSub) {
      this._stateSub.unsubscribe();
      this._stateSub = null;
      writeSceneLog(
        "UrlSyncManager",
        "Root deactived, unsub to state",
        "same key",
        this._sceneRoot.state.key === root.state.key
      );
    }
  }
  syncFrom(sceneObj) {
    const urlParams = locationService.getSearch();
    this.urlKeyMapper.rebuildIndex(this._sceneRoot);
    this._syncSceneStateFromUrl(sceneObj, urlParams);
  }
  _syncSceneStateFromUrl(sceneObject, urlParams) {
    if (sceneObject.urlSync) {
      const urlState = {};
      const currentState = sceneObject.urlSync.getUrlState();
      for (const key of sceneObject.urlSync.getKeys()) {
        const uniqueKey = this.urlKeyMapper.getUniqueKey(key, sceneObject);
        const newValue = urlParams.getAll(uniqueKey);
        const currentValue = currentState[key];
        if (isUrlValueEqual(newValue, currentValue)) {
          continue;
        }
        if (newValue.length > 0) {
          if (Array.isArray(currentValue)) {
            urlState[key] = newValue;
          } else {
            urlState[key] = newValue[0];
          }
        } else {
          urlState[key] = null;
        }
      }
      if (Object.keys(urlState).length > 0) {
        sceneObject.urlSync.updateFromUrl(urlState);
      }
    }
    sceneObject.forEachChild((child) => this._syncSceneStateFromUrl(child, urlParams));
  }
  getUrlState(root) {
    const urlKeyMapper = new UniqueUrlKeyMapper();
    urlKeyMapper.rebuildIndex(root);
    const result = {};
    const visitNode = (obj) => {
      if (obj.urlSync) {
        const newUrlState = obj.urlSync.getUrlState();
        for (const [key, value] of Object.entries(newUrlState)) {
          if (value != null) {
            const uniqueKey = urlKeyMapper.getUniqueKey(key, obj);
            result[uniqueKey] = value;
          }
        }
      }
      obj.forEachChild(visitNode);
    };
    visitNode(root);
    return result;
  }
}
class UniqueUrlKeyMapper {
  constructor() {
    this.index = /* @__PURE__ */ new Map();
  }
  getUniqueKey(key, obj) {
    const objectsWithKey = this.index.get(key);
    if (!objectsWithKey) {
      throw new Error("Cannot find any scene object that uses the key '" + key + "'");
    }
    const address = objectsWithKey.findIndex((o) => o.sceneObject === obj);
    if (address > 0) {
      return `${key}-${address + 1}`;
    }
    return key;
  }
  rebuildIndex(root) {
    this.index.clear();
    this.buildIndex(root, 0);
  }
  buildIndex(sceneObject, depth) {
    if (sceneObject.urlSync) {
      for (const key of sceneObject.urlSync.getKeys()) {
        const hit = this.index.get(key);
        if (hit) {
          hit.push({ sceneObject, depth });
          hit.sort((a, b) => a.depth - b.depth);
        } else {
          this.index.set(key, [{ sceneObject, depth }]);
        }
      }
    }
    sceneObject.forEachChild((child) => this.buildIndex(child, depth + 1));
  }
}
function isUrlValueEqual(currentUrlValue, newUrlValue) {
  if (currentUrlValue.length === 0 && newUrlValue == null) {
    return true;
  }
  if (!Array.isArray(newUrlValue) && (currentUrlValue == null ? void 0 : currentUrlValue.length) === 1) {
    return newUrlValue === currentUrlValue[0];
  }
  if ((newUrlValue == null ? void 0 : newUrlValue.length) === 0 && currentUrlValue === null) {
    return true;
  }
  return isEqual(currentUrlValue, newUrlValue);
}
let urlSyncManager;
function getUrlSyncManager() {
  if (!urlSyncManager) {
    urlSyncManager = new UrlSyncManager();
  }
  return urlSyncManager;
}

export { UrlSyncManager, getUrlSyncManager, isUrlValueEqual };
//# sourceMappingURL=UrlSyncManager.js.map
